import React from 'react';
import { Row, Col, Divider, Space } from 'antd';
import styled, { css } from 'styled-components';
import Link from 'next/link';
import getConfig from 'next/config';
import EventDate from 'components/EventDate';
import NImage from 'next/image';
import Image from 'components/Image';
import PatronatImage from 'public/static/images/patronat.svg';
import Title from 'components/Card/Title';
import HTMLContent from 'components/HTMLContent';

const Date = styled.h5`
    font-size: 1.2rem;
    width: 100%;
    font-weight: 300;
    line-height: 26px;
    margin: 0;
    a {
        color: ${(props) => props.color || props.theme.colors.brownishGrey};
    }
`;

const EventType = styled(Link)`
    text-transform: capitalize;
    font-weight: 400;
    color: ${(props) => props.color || props.theme.colors.greyishBrownTwo};
`;

const Branch = styled.a`
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: 0.14px;
    text-align: left;
    color: ${(props) => props.color || props.theme.colors.greyishBrownTwo};
`;

const BranchRow = styled(Col)`
    padding: 30px;
    background-color: ${({ theme }) => theme.colors.grey};
`;

const BranchTitle = styled.span`
    margin-right: 20px;
    margin-top: 0;
    font-size: 1.2rem;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    color: ${(props) => props.color || props.theme.colors.primary};
`;

const PatronateWrapper = styled.div`
    position: absolute;
    right: 10px;
    top: 10px;
`;

const Text = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    word-wrap: break-word;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;

    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    padding-top: 40px;
    color: ${(props) => props.color || props.theme.colors.brownishGrey};
    @media (max-width: 992px) {
        display: none;
    }
    a {
        color: ${(props) => props.color || props.theme.colors.greyishBrownTwo};
    }
`;

type StyledTitleProps = {
    $patronate?: boolean;
};

const StyledTitle = styled(Title)<StyledTitleProps>`
    ${({ $patronate }) =>
        $patronate &&
        css`
            @media (min-width: 992px) {
                width: 80%;
            }
        `}
`;

const MobileVAlignMiddleCol = styled(Col)`
    @media (max-width: 992px) {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
`;

const getRootLevelBranches = (branches: Branch[]): Branch[] => {
    const getRootBranchTitle = (branch) => {
        if (branch.lvl > 1) {
            return getRootBranchTitle(branch.parent);
        }

        return { title: branch.title, seoSlug: branch.seoSlug };
    };
    return [
        ...new Set<string>(branches.map((branch) => JSON.stringify(getRootBranchTitle(branch))))
    ].map((branch: string): Branch => JSON.parse(branch));
};

const Item = ({ item }: { item: Occurrence }): JSX.Element => {
    const { brand } = getConfig().publicRuntimeConfig;
    const branches = getRootLevelBranches(item.branches);
    return (
        <Row gutter={[16, 16]}>
            <Col span={8}>
                <Row align="middle" justify="center" style={{ height: '100%' }}>
                    <Col>
                        <Link href={`/wydarzenia/${item.seoSlug}`}>
                            <Image
                                type="logo"
                                alt={item.name}
                                height={268}
                                width={268}
                                src={item.logo}
                                style={{ maxWidth: '100%', objectFit: 'contain' }}
                            />
                        </Link>
                    </Col>
                </Row>
            </Col>
            <MobileVAlignMiddleCol span={16}>
                <StyledTitle $patronate={item.patronate}>
                    <Link href={`/wydarzenia/${item.seoSlug}`}>{item.name}</Link>
                </StyledTitle>
                <Date>
                    <EventDate startDate={item.startDate} endDate={item.endDate} />
                    <Divider type="vertical" />
                    <EventType href={`/wydarzenia?page=1&type=${item.type}`}>{item.type}</EventType>
                </Date>

                <Text>
                    <HTMLContent content={item.description} />
                </Text>
                {item.patronate && (
                    <PatronateWrapper>
                        <NImage
                            alt={`${brand} Patronat`}
                            src={PatronatImage}
                            width={120}
                            height={22}
                        />
                    </PatronateWrapper>
                )}
            </MobileVAlignMiddleCol>
            {branches.length > 0 && (
                <BranchRow span={24}>
                    <Row>
                        <Col xs={24} md={8}>
                            <BranchTitle>Branże powiązane z wydarzeniem:</BranchTitle>
                        </Col>
                        <Col xs={24} md={16}>
                            <Space split={<Divider type="vertical" />} wrap size={[8, 2]}>
                                {branches.map((branch) => (
                                    <Branch
                                        key={branch.seoSlug}
                                        href={`/wydarzenia?branch=${branch.seoSlug}`}
                                    >
                                        {branch.title}
                                    </Branch>
                                ))}
                            </Space>
                        </Col>
                    </Row>
                </BranchRow>
            )}
        </Row>
    );
};

export default Item;
